import React from "react"
import { useQuery } from "@apollo/client"
import { useHistory } from "react-router-dom"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { connect, TCExtensionAPI } from "trimble-connect-project-workspace-api"
import locationToOrigin from "../util/locationToOrigin"
import {
  extensionApiState,
  projectIdState,
  projectOriginState,
  quadriProjectIdState,
  featureCatalogIdState,
  signedInUserState,
  userClaimsState,
  projectNameState,
  OwnershipNameState,
  signedInUserIdState,
  projectRegionState
} from "./StateSelectors"
import { PendingSpinner } from "./Pending"
import { GET_PROJECT_STATUS } from "../api/QuadriApi"
import Oops from "./Oops/Oops"
import { setMenu } from "../util/setMenu"
import { useTranslation } from "react-i18next"
import { exceptionFromApolloError } from "../util/apolloError"
import {
  SetEnvironmentDefault,
  SetEnvironmentProdEU,
  SetEnvironmentProdUS,
  SetEnvironmentProdAUS,
  SetEnvironmentProdAP
} from "../environments"
import { ApolloProvider } from "@apollo/client"
import { QuadriApi } from "../api/QuadriApi"
import { identify } from "../util/gainsight"
import { getRegions } from "../api/QuadriSettingsApi"

interface ConnectExtensionProps {
  children: React.ReactElement
}

const ConnectExtension = (props: ConnectExtensionProps) => {
  const [extensionAuthorized, setExtensionAuthorized] = React.useState(true)
  const [extensionApi, setExtensionApi] = useRecoilState(extensionApiState)
  const [projectId, setProjectId] = useRecoilState(projectIdState)
  const [, setProjectName] = useRecoilState(projectNameState)
  const [projectRegion, setProjectRegion] = useRecoilState(projectRegionState)
  const [, setUserClaims] = useRecoilState(userClaimsState)
  const setProjectOrigin = useSetRecoilState(projectOriginState)
  const [, setSignedInUser] = useRecoilState(signedInUserState)
  const [, setSignedInUserId] = useRecoilState(signedInUserIdState)
  const [isProjectRegionInList, setIsProjectRegionInList] = React.useState<boolean | null>(null)
  const { i18n } = useTranslation()
  const { t } = useTranslation("common")
  // Flag to delay rendering of the rest of the app until the correct region has been set
  const [connectProjectFetched, setConnectProjectFetched] = React.useState(false)

  const [reloading, setReloading] = React.useState(false)

  const isIframe = window !== window.parent

  const history = useHistory()

  const handleCommand = async (command: string) => {
    // remove the query string from the URL
    const baseCommand = command.split("?")[0]
    const query = command.split("?")[1]

    switch (baseCommand) {
      case "config":
        history.replace("/config")
        // Workround for missing notifications from Connect
        window.location.reload()
        setReloading(true)
        break
      case "top_menu":
        history.replace("/objectmodelconfig")
        break
      case "tasks":
        history.replace("/tasks")
        break
      case "timeline":
        history.replace("/timeline")
        break
      case "worksets":
        history.replace("/worksets" + (query ? "?" + query : ""))
        break
      case "objectmodelconfig":
        history.replace("/objectmodelconfig")
        window.dispatchEvent(new CustomEvent("objectModelConfig:open"))
        break
      default:
        console.log("Got unknown command: ", command)
    }
  }

  const setAccessToken = (accessToken?: string) => {
    if (accessToken) {
      if (accessToken === "Denied") {
        setExtensionAuthorized(false)
        return
      }
      const payload = accessToken.split(".")[1]
      if (payload) {
        localStorage.setItem("tc-access-token", accessToken)
        const claims = JSON.parse(window.atob(payload))
        identify(claims)
        setUserClaims(claims)
      }
    }
  }

  React.useEffect(() => {
    const asyncStuff = async () => {
      const extApi = await connect(
        window.parent,
        (event, args) => {
          //console.log("Extensions API event: ", event, " With args: ", args)
          switch (event) {
            case "extension.command":
              handleCommand(args.data)
              break
            case "extension.accessToken":
              console.log("Got access token refresh event!")
              setAccessToken(args.data)
              break
            case "extension.userSettingsChanged":
              //"User settings changed!"
              break
          }
        },
        30000
      )
      setExtensionApi(extApi)
      setMenu(t, undefined, extApi)

      // TODO: Is this an error in the Typescript files, or in the documentation. See
      // https://components.connect.trimble.com/trimble-connect-project-workspace-api/docs/index.html
      // Confirmed that this is an error in the documentation
      const accessTokenPromise = extApi.extension.getPermission("accesstoken") as unknown as Promise<string>
      const projectPromise = extApi.project.getCurrentProject()
      const languagePromise = extApi.user.getUserSettings().then((s: any) => {
        console.log("user settings: ", s)
        setSignedInUser(s.email)
        setSignedInUserId(s.id)
        return i18n.changeLanguage(getLangCodeIso639_1(s.language))
      })

      const [accessToken, project] = await Promise.all([accessTokenPromise, projectPromise, languagePromise])

      setAccessToken(accessToken)
      setProjectId(project.id)
      setProjectName(project.name)
      setProjectRegion(project.location)
      console.log("project: ", project)

      if (project.location) {
        console.log("Project location: ", project.location)
        if (project.location === "europe") {
          SetEnvironmentProdEU()
        } else if (project.location === "northAmerica") {
          SetEnvironmentProdUS()
        } else if (project.location === "australia") {
          SetEnvironmentProdAUS()
        } else if (project.location === "asia") {
          SetEnvironmentProdAP()
        } else {
          console.error("Unknown project location: ", project.location)
          SetEnvironmentDefault()
        }
        setProjectOrigin(locationToOrigin(project.location))
      } else {
        console.error("Project location not set!")
        SetEnvironmentDefault()
      }

      // Make sure that the correct environment has been set
      setConnectProjectFetched(true)

      const regions = await getRegions(accessToken ?? "")
      if (regions && regions.regions) {
        setIsProjectRegionInList(regions.regions.some((r: any) => r.name === project.location))
      }
    }

    asyncStuff()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!extensionAuthorized) {
    extensionApi?.extension.setStatusMessage(t("notAuthorized.heading"))
    return <Oops heading={t("notAuthorized.heading")} body={t("notAuthorized.body")} tryAgainDisabled={true} />
  }

  if (isProjectRegionInList !== null && !isProjectRegionInList) {
    return (
      <Oops
        heading={t("regionNotSupported.heading")}
        body={t("regionNotSupported.body", {
          region: projectRegion
        })}
        tryAgainDisabled={true}
      />
    )
  }

  // Check if we have a token yet
  const token = localStorage.getItem("tc-access-token")

  return token && extensionApi && projectId && !reloading && connectProjectFetched ? ( // For running in Connect
    <ApolloProvider client={new QuadriApi().getClient()}>
      <ConnectExtensionInner extensionApi={extensionApi} content={props.children} />
    </ApolloProvider>
  ) : (
    <PendingSpinner />
  )
}

interface ConnectExtensionInnerProps {
  extensionApi: TCExtensionAPI
  content: React.ReactElement
}

const ConnectExtensionInner = (props: ConnectExtensionInnerProps) => {
  const projectId = useRecoilValue(projectIdState)
  const { t } = useTranslation("common")

  if (!projectId) {
    throw new Error("Connect project ID need to be set before loading project status")
  }

  const [, setQuadriProjectId] = useRecoilState(quadriProjectIdState)
  const [, setFeatureCatalogId] = useRecoilState(featureCatalogIdState)
  const [signedInUser] = useRecoilState(signedInUserState)
  const [loaded, setLoaded] = React.useState(false)
  const [, setProjectOwnership] = useRecoilState(OwnershipNameState)

  const history = useHistory()

  React.useEffect(() => {
    history.listen((location: any, action: any) => {
      // The below way of setting the active menu item does not work, because not only
      // does it set the active menu item, but it also navigates to that submenu. We need
      // a way to just update the UI without affecting internal page navigation
      // // command, ie "tasks"
      // const command = location.pathname.split("/")[1]
      // props.extensionApi.ui.setActiveMenuItem(command)
    })
  }, [history])

  const {
    error,
    data,
    loading: projectLoading
  } = useQuery(GET_PROJECT_STATUS, {
    //=> to abe able to show status on the button in extension page (ready to active etc..) might have to keep it. (TCPS calls)
    //skip: !hasProjectAccess,
    variables: { id: projectId },
    onCompleted: (data) => {
      // This query sets the `quadri project ID` used in subsequent queries to GraphQL
      const response = data.tryGetConnectQuadriProject
      if (response.found) {
        console.log("Project found: ", response.project)
        setQuadriProjectId(response.project.quadriProjectId)
        console.log("Set Quadri Project Id: ", response.project.quadriProjectId)
        setFeatureCatalogId(response.project.featureCatalogId)
        console.log("Set Feature Catalogue Id: ", response.project.featureCatalogId)
        setProjectOwnership(response.project.customerAccountName)

        const status = response.project.projectStatus
        setMenu(t, status, props.extensionApi)
      }
      setLoaded(true) // This can only be set after quadri project id.
    }
  })

  const getProjectError = data?.tryGetConnectQuadriProject?.error

  if (error) {
    const errorMessage = exceptionFromApolloError(error).message
    console.error(errorMessage)
    props.extensionApi.extension.setStatusMessage(t("failedToFetchStatus"))
    return <Oops body={errorMessage} />
  } else if (getProjectError) {
    props.extensionApi.extension.setStatusMessage(t("failedToFetchStatus"))
    return <Oops body={getProjectError} />
  } else if (!loaded || projectLoading) {
    props.extensionApi.extension.setStatusMessage(t("fetchingStatus"))
    return <PendingSpinner />
  } else {
    const isFound = data?.tryGetConnectQuadriProject?.found
    const projectStatus = data?.tryGetConnectQuadriProject?.project?.projectStatus

    if (isFound && projectStatus) {
      props.extensionApi.extension.setStatusMessage(t("projectStatus." + projectStatus))
    } else {
      props.extensionApi.extension.setStatusMessage(t("readyToEnable"))
    }
    return props.content
  }
}

const getLangCodeIso639_1 = (language: string) => language.split("-")[0]

export default ConnectExtension
